import { Link } from "react-router-dom";
import { cn } from "../../lib/utils";
import type { HeaderNavLink } from "../Header/components/headerNavMeta";

export function isNavPathActive(pathname: string, href: string): boolean {
  if (href === "/relatorios") return pathname === "/relatorios";
  return pathname === href || pathname.startsWith(`${href}/`);
}

type NavRichLinkProps = {
  item: HeaderNavLink;
  pathname: string;
  compact?: boolean;
  iconOnly?: boolean;
  onNavigate?: () => void;
};

export function NavRichLink({
  item,
  pathname,
  compact = false,
  iconOnly = false,
  onNavigate,
}: NavRichLinkProps) {
  const Icon = item.icon;
  const active = isNavPathActive(pathname, item.href);

  if (iconOnly) {
    return (
      <Link
        to={item.href}
        onClick={onNavigate}
        title={item.title}
        aria-label={item.title}
        className={cn(
          "group relative flex items-center justify-center rounded-xl p-1.5 no-underline outline-none transition-all duration-200",
          active
            ? "bg-primary/[0.12] shadow-[inset_0_1px_0_rgba(255,255,255,0.55),0_6px_14px_rgba(139,0,0,0.12)]"
            : "hover:-translate-y-0.5 hover:bg-white hover:shadow-[0_8px_16px_rgba(15,23,42,0.1)]"
        )}
      >
        <span
          className={cn(
            "inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors",
            active
              ? "bg-primary/15 text-primary"
              : "bg-primary/10 text-primary group-hover:bg-primary/15"
          )}
        >
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
      </Link>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={cn(
        "group relative block select-none rounded-lg no-underline outline-none transition-all duration-150",
        compact ? "px-2.5 py-2 max-lg:py-2.5" : "px-2.5 py-2.5",
        active
          ? "bg-primary/[0.08] text-accent-foreground shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]"
          : "text-neutral-800 hover:bg-neutral-100/80 hover:shadow-sm"
      )}
    >
      <div
        className={cn(
          "flex gap-2.5",
          compact ? "items-start max-lg:items-center" : "items-center"
        )}
      >
        <span
          className={cn(
            "inline-flex shrink-0 items-center justify-center rounded-md transition-colors",
            compact ? "mt-0.5 h-7 w-7 max-lg:mt-0" : "h-8 w-8",
            active
              ? "bg-primary/15 text-primary"
              : "bg-primary/10 text-primary group-hover:bg-primary/15"
          )}
        >
          <Icon
            className={cn(compact ? "h-3.5 w-3.5" : "h-4 w-4")}
            strokeWidth={2}
          />
        </span>
        <span
          className={cn(
            "min-w-0 flex-1 text-sm font-medium tracking-tight",
            compact ? "break-words leading-snug" : "truncate leading-none",
            active ? "text-primary" : "text-neutral-800"
          )}
        >
          {item.title}
        </span>
      </div>
      {!compact && item.description ? (
        <p className="mt-1.5 line-clamp-2 pl-[2.625rem] text-[11px] leading-snug text-neutral-500">
          {item.description}
        </p>
      ) : null}
    </Link>
  );
}
